import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const fastColorSwap = this.#prepareFastStussyColorSwap(event.target);

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        // For STÜSSY color changes, the current two-slide gallery is updated in place.
        // Do not replace it with freshly rendered lazy-loaded markup, because that was
        // causing the selected hoodie to appear briefly and then disappear into a blank gallery.
        if (fastColorSwap && (await fastColorSwap)) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #prepareFastStussyColorSwap(target) {
    const picker = target.closest('variant-picker');
    if (!(picker instanceof HTMLElement) || picker.dataset.productId !== '9098108240070') return null;

    const optionName =
      target instanceof HTMLSelectElement
        ? target.selectedOptions[0]?.dataset?.optionName
        : target.dataset?.optionName;

    if (optionName !== 'Color') return null;

    const color =
      target instanceof HTMLSelectElement
        ? target.value
        : target instanceof HTMLInputElement
        ? target.value
        : '';

    if (!color) return null;

    const productMedia = window.__dripstockPairedColorMedia?.[picker.dataset.productId];
    if (!Array.isArray(productMedia)) return null;

    const findSide = (side) =>
      productMedia.find((item) => {
        const parts = String(item.alt || '').split(' | ');
        return parts[1] === color && parts[2] === side;
      });

    const front = findSide('Front');
    const back = findSide('Back');
    if (!front) return null;

    const useDesktop = window.matchMedia('(min-width: 750px)').matches;
    picker.dataset.fastSwapColor = color;

    const load = (item) =>
      new Promise((resolve) => {
        if (!item) {
          resolve(false);
          return;
        }

        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = 'high';
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = useDesktop ? item.desktop : item.mobile;
        if (image.complete) resolve(true);
      });

    const apply = (item, index) => {
      if (!item || !this.isConnected || picker.dataset.fastSwapColor !== color) return;

      const scroller = this.querySelector('slideshow-slides');
      if (!scroller) return;

      const slides = Array.from(scroller.children).filter(
        (child) => child.tagName === 'SLIDESHOW-SLIDE'
      );
      const image = slides[index]?.querySelector('.product-media__image');
      if (!(image instanceof HTMLImageElement)) return;

      const url = useDesktop ? item.desktop : item.mobile;
      image.removeAttribute('srcset');
      image.src = url;
      image.alt = item.alt || '';
      image.loading = 'eager';
      image.fetchPriority = 'high';
      image.dataset.maxResolution = item.desktop || url;
      // Keep STÜSSY image thumbnails consistent with the chosen color.
      for (const controls of this.querySelectorAll('slideshow-controls[thumbnails]')) {
        const thumb = controls.querySelectorAll('.slideshow-controls__thumbnail img')[index];
        if (!(thumb instanceof HTMLImageElement)) continue;
        thumb.removeAttribute('srcset');
        thumb.src = item.mobile || url;
        thumb.alt = item.alt || '';
      }
    };

    // Load the front and back at the same time, but keep the current color visible
    // until the whole pair is ready. This prevents a customer from swiping to an
    // unloaded second slide and seeing a blank gallery.
    const frontPromise = load(front);
    const backPromise = back ? load(back) : Promise.resolve(true);

    const swap = Promise.all([frontPromise, backPromise]).then(([frontLoaded, backLoaded]) => {
      if (!frontLoaded || picker.dataset.fastSwapColor !== color) return false;
      if (back && !backLoaded) return false;

      apply(front, 0);
      if (back) apply(back, 1);
      this.slideshow?.select(0, undefined, { animate: false });
      return true;
    });

    return swap;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
